import { AppBar, Box, useTheme, IconButton } from '@mui/material';
import LanguageSelector from '@act/react-components/consents/member-view/LanguageSelector';
import { AppHeaderLogo } from './AppHeaderLogo';
import { AppHeaderLogoMark } from './AppHeaderLogoMark';
import { useMobile } from '@act/utilities/MobileContext';
import { useSideMenuDrawer } from '@act/utilities/SideMenuDrawerContext';
import { IconLibrary } from '../display/IconLibrary/IconLibrary';

interface AppHeaderProps {
  href?: string;
  withLogo?: boolean;
  disableAnimations?: boolean;
  disableMobileMenu?: boolean;
  /** When false, the logo renders without a link (e.g. consent enrollment error). */
  linkLogo?: boolean;
}

export const AppHeader = ({
  href = '/',
  withLogo = false,
  disableAnimations = false,
  disableMobileMenu = false,
  linkLogo = true,
}: AppHeaderProps): JSX.Element => {
  const theme = useTheme();
  const { isMobile } = useMobile();
  const { toggleDrawer } = useSideMenuDrawer();

  const shouldCenterLogo = isMobile && disableMobileMenu;

  return (
    <Box>
      <AppBar
        elevation={0}
        position="static"
        sx={{
          padding: isMobile
            ? '0 8px'
            : {
                xs: '16px 24px',
                sm: '24px 32px',
                md: `8px 16px 8px 0`,
              },

          display: 'flex',
          flexDirection: 'row',
          justifyContent: shouldCenterLogo ? 'center' : 'space-between',
          alignItems: {
            xs: 'center',
            sm: 'center',
            md: 'center',
          },
          minHeight: {
            xs: '64px',
            md: '90px',
          },
          height: '90px',
          boxSizing: 'border-box',

          color: theme.palette.primary.main,
          borderBottom: `1px solid ${theme.palette.strokePrimary}`,
          backgroundColor: theme.palette.primary.contrastText,
        }}
      >
        {withLogo &&
          (linkLogo ? (
            <AppHeaderLogo href={href} disableAnimations={disableAnimations} />
          ) : (
            <AppHeaderLogoMark disableAnimations={disableAnimations} />
          ))}
        {isMobile && !disableMobileMenu && (
          <IconButton
            onClick={toggleDrawer}
            sx={{
              color: theme.palette.primary.main,
              marginLeft: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
            aria-label="Toggle navigation menu"
          >
            <IconLibrary iconName="List" weight="regular" size={28} />
          </IconButton>
        )}
        {!shouldCenterLogo && <Box id="header-portal" sx={{ width: '100%' }} />}
        {(!isMobile || disableMobileMenu) && (
          <Box
            sx={{
              ...(shouldCenterLogo
                ? {
                    position: 'absolute',
                    right: '8px',
                  }
                : {
                    marginRight: '16px',
                  }),
            }}
          >
            <LanguageSelector />
          </Box>
        )}
      </AppBar>
    </Box>
  );
};

export default AppHeader;
